import { FC, Fragment } from 'react';
import {
  ComponentRendering,
  Image,
  ImageField,
  RichTextField,
  TextField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import {
  ComponentProps,
  getContrastThemeClass,
  resolveBackgroundStyle,
} from '@gn-audio/jss-components';

import styles from './BrandStickers.module.css';

type TextBlock = {
  text: RichTextField;
  fontSize: TextField;
};

type ImageBlock = {
  image: ImageField;
};

type Block = { id: string } & (TextBlock | ImageBlock);

type BrandSticker = {
  id: string;
  background: TextField;
  blocks: Block[];
};

type BrandStickersSpotFields = {
  background: TextField;
  stickers: BrandSticker[];
};

type BrandStickersSpotProps = {
  fields: BrandStickersSpotFields;
} & ComponentProps;

const BrandStickersSpot: FC<BrandStickersSpotProps> = ({ fields }) => {
  const { background, stickers } = fields;
  if (!stickers?.length) return null;

  const spotBackgroundStyle = resolveBackgroundStyle(background.value as string);

  return (
    <div className="container-fluid !py-0" style={spotBackgroundStyle}>
      <div className="container">
        <div className="flex flex-col flex-wrap items-center justify-center gap-16 md:flex-row">
          {stickers.map(({ id, background, blocks }) => {
            const contrastTheme = getContrastThemeClass(background.value as string);
            const backgroundStyle = resolveBackgroundStyle(background.value as string);

            return (
              <div
                key={id}
                className={classNames(
                  'relative aspect-square w-full max-w-80 select-none overflow-hidden rounded-full',
                  contrastTheme
                )}
                style={backgroundStyle}
              >
                <div className="flex size-full flex-col items-center justify-center gap-[.5cqw] rounded-full p-2 text-center uppercase leading-[1] [container-type:inline-size]">
                  {blocks.map((block) => {
                    const isTextBlock = 'text' in block && 'fontSize' in block;
                    const isImageBlock = 'image' in block;
                    const isSeparatorBlock = !isTextBlock && !isImageBlock;

                    return (
                      <Fragment key={block.id}>
                        {isTextBlock && !!block.text.value && (
                          <div
                            style={{ fontSize: block.fontSize.value }}
                            dangerouslySetInnerHTML={{
                              __html: block.text.value,
                            }}
                          />
                        )}
                        {isImageBlock && !!block.image && (
                          <Image className="max-h-16 w-auto" field={block.image} loading="lazy" />
                        )}
                        {isSeparatorBlock && (
                          <hr
                            className={classNames(
                              'my-[1cqw] h-[.7cqw] w-1/3 border-0',
                              contrastTheme === 'light' ? 'bg-black' : 'bg-white'
                            )}
                          />
                        )}
                      </Fragment>
                    );
                  })}
                </div>
                <div
                  className={classNames(
                    'absolute inset-0 z-10',
                    'h-[200%] w-[200%]',
                    'pointer-events-none select-none',
                    '-translate-x-full -translate-y-full rotate-45',
                    '[background-image:linear-gradient(to_right,transparent,hsla(0,0%,100%,0.3),transparent)]',
                    styles.shimmerEffect
                  )}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
export const getStaticProps = async ({ fields }: ComponentRendering<BrandStickersSpotFields>) => {
  try {
    if (!fields || !Object.keys(fields).length)
      throw new Error('No fields found for BrandStickersSpot');

    const refinedStickers =
      fields.stickers?.map((sticker) => ({
        ...sticker,
        blocks: sticker.blocks.map((block) => {
          const isTextBlock = 'text' in block && 'fontSize' in block;
          if (!isTextBlock) return block;

          return {
            ...block,
            text: {
              ...block.text,
              value: sanitize((block.text?.value as string) || ''),
            },
          };
        }),
      })) || [];

    return {
      fields: {
        ...fields,
        stickers: refinedStickers,
      },
    };
  } catch (error) {
    console.error(error);

    return {
      fields: {
        background: '',
        stickers: [],
      },
    };
  }
};

export default withDatasourceCheck()<BrandStickersSpotProps>(BrandStickersSpot);
